'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  applyBrandingToDocument,
  Branding,
  DEFAULT_BRANDING,
  fetchPublicBranding,
  readCachedBranding,
  writeCachedBranding,
} from '@/lib/branding'

interface BrandingContextValue {
  branding: Branding
  /** Apply a branding the server just returned (e.g. after the admin saves). */
  setBranding: (branding: Branding) => void
  refresh: () => Promise<void>
}

const BrandingContext = createContext<BrandingContextValue | null>(null)

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [branding, setBrandingState] = useState<Branding>(DEFAULT_BRANDING)

  const setBranding = useCallback((next: Branding) => {
    setBrandingState(next)
    applyBrandingToDocument(next)
    writeCachedBranding(next)
  }, [])

  const refresh = useCallback(async () => {
    try {
      setBranding(await fetchPublicBranding())
    } catch {
      // Keep the cached/default branding if the API is unreachable.
    }
  }, [setBranding])

  useEffect(() => {
    // Paint the last known branding immediately, then reconcile with the server.
    const cached = readCachedBranding()
    if (cached) {
      setBrandingState(cached)
      applyBrandingToDocument(cached)
    }
    void refresh()
  }, [refresh])

  const value = useMemo(
    () => ({ branding, setBranding, refresh }),
    [branding, setBranding, refresh]
  )

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>
}

export function useBranding(): BrandingContextValue {
  const context = useContext(BrandingContext)
  if (!context) throw new Error('useBranding must be used within BrandingProvider')
  return context
}
